import { newId } from '@dd/shared';
import { GlobalDB, ShopDB } from './schema';

export const globalDb = new GlobalDB();

let current: ShopDB | null = null;
let currentName: string | null = null;
const subs = new Set<() => void>();

export const dbName = (shopId: string, role: string): string => `dd2-${shopId}-${role === 'cashier' ? 'cashier' : 'full'}`;

/** Open (or switch) the shop database. The role class decides which copy of the data this device holds. */
export function openShopDb(shopId: string, role: string): ShopDB {
  const name = dbName(shopId, role);
  if (current && currentName === name) return current;
  current?.close();
  current = new ShopDB(name);
  currentName = name;
  subs.forEach((f) => f());
  return current;
}
export function closeShopDb(): void {
  current?.close();
  current = null;
  currentName = null;
  subs.forEach((f) => f());
}
export const hasDb = (): boolean => current !== null;
export function db(): ShopDB {
  if (!current) throw new Error('shop database is not open');
  return current;
}
export const dbKey = (): string => currentName ?? 'none';
export const onDbChange = (f: () => void): (() => void) => { subs.add(f); return () => subs.delete(f); };

// ── device-wide key/value ─────────────────────────────────────────────────────
export async function kvGet<T>(key: string): Promise<T | undefined> {
  return (await globalDb.kv.get(key))?.value as T | undefined;
}
export async function kvSet(key: string, value: unknown): Promise<void> {
  await globalDb.kv.put({ key, value });
}

export async function deviceId(): Promise<string> {
  let id = await kvGet<string>('deviceId');
  if (!id) {
    try { id = localStorage.getItem('dd.deviceId') ?? undefined; } catch { /* ignore */ }
  }
  if (!id) id = newId();
  await kvSet('deviceId', id);
  try { localStorage.setItem('dd.deviceId', id); } catch { /* ignore */ }
  return id;
}

// ── shop-level meta ──────────────────────────────────────────────────────────
export async function metaGet<T>(key: string): Promise<T | undefined> {
  return (await db().meta.get(key))?.value as T | undefined;
}
export async function metaSet(key: string, value: unknown): Promise<void> {
  await db().meta.put({ key, value });
}

/** Ask the browser not to evict our data (Guide §10.8). Result is remembered for the Settings screen. */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (navigator.storage?.persist) {
      if (await navigator.storage.persisted()) return true;
      return await navigator.storage.persist();
    }
  } catch { /* ignore */ }
  return false;
}
